import { Box, Center, Flex, IconButton, Input, Select, useBreakpointValue } from '@chakra-ui/react';
import React from 'react';
import { DateRangeType } from './DateRangeTypePicker';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { getInitialMonthValue, INITIAL_SELECTED_PERIOD_STATE } from './fleet-dashboard-helpers';
import { formatDate } from '../../utils/datetime-utils';
import { CalendarIcon } from '../../styles/custom-icons';
import { FIRST_YEAR_OF_DATA } from './fleet-dashboard-helpers';

type Props = {
  rangeType: DateRangeType;
  selectedPeriod: string;
  onChangeSelectedPeriod: (newPeriod: string) => void;
  isDisabled: boolean;
};

type Direction = 'forward' | 'back';

function getYearOptions() {
  const currentYear = new Date().getFullYear();
  const yearsArray = [];

  for (let year = FIRST_YEAR_OF_DATA; year <= currentYear; year++) {
    yearsArray.push({ label: year, value: year });
  }

  return yearsArray;
}

const YEAR_OPTIONS = getYearOptions();

export default function PeriodPicker({ selectedPeriod, onChangeSelectedPeriod, rangeType, isDisabled }: Props) {
  const isMobileViewport = useBreakpointValue({
    base: true,
    xl: false,
  });

  const commonInputProps = {
    width: '160px',
    value: selectedPeriod,
    isDisabled,
  };

  const selectedRangeTypeToInputComponent: Record<DateRangeType, JSX.Element> = {
    [DateRangeType.Day]: (
      <Box position={'relative'}>
        <Input
          data-testid={'fleet-dashboard-date-input-day'}
          required // Disables the "Clear" button
          {...commonInputProps}
          onChange={(e) => onChangeSelectedPeriod(e.currentTarget.value)}
          border={'none'}
          max={formatDate(new Date())}
          type={'date'}
        />
        <CalendarIcon position={'absolute'} right={4} top={'30%'} />
      </Box>
    ),
    [DateRangeType.Week]: (
      <Box position={'relative'}>
        <Input
          data-testid={'fleet-dashboard-date-input-week'}
          required // Disables the "Clear" button
          {...commonInputProps}
          onChange={(e) => onChangeSelectedPeriod(e.currentTarget.value)}
          border={'none'}
          max={formatDate(new Date())}
          type={'date'}
        />
        <CalendarIcon position={'absolute'} right={4} top={'30%'} />
      </Box>
    ),
    [DateRangeType.Month]: (
      <Box position={'relative'}>
        <Input
          data-testid={'fleet-dashboard-date-input-month'}
          required // Disables the "Clear" button
          {...commonInputProps}
          onChange={(e) => onChangeSelectedPeriod(e.currentTarget.value)}
          border={'none'}
          max={getInitialMonthValue()}
          type={'month'}
        />
        <CalendarIcon position={'absolute'} right={4} top={'30%'} />
      </Box>
    ),
    [DateRangeType.Year]: (
      <Select
        data-testid={'fleet-dashboard-date-input-year'}
        {...commonInputProps}
        onChange={(e) => e.currentTarget.value && onChangeSelectedPeriod(e.currentTarget.value)}
        placeholder="Select a year"
      >
        {YEAR_OPTIONS.map(({ label, value }) => (
          <option key={`${label}-${value}`} value={value}>
            {label}
          </option>
        ))}
      </Select>
    ),
    [DateRangeType.All]: (
      <Center w="100%">
        <Box>All</Box>
      </Center>
    ),
  };

  const directionToFunction: Record<Direction, (a: number, b: number) => number> = {
    forward: (a: number, b: number) => a + b,
    back: (a: number, b: number) => a - b,
  };

  function handleMoveByDay(direction: Direction) {
    const fn = directionToFunction[direction];
    const selectedDate = new Date(selectedPeriod);
    selectedDate.setDate(fn(selectedDate.getDate(), 1));
    const selectedDateFormatted = formatDate(selectedDate);

    onChangeSelectedPeriod(selectedDateFormatted);
  }

  function handleMoveByWeek(direction: Direction) {
    const fn = directionToFunction[direction];
    const selectedDate = new Date(selectedPeriod);
    selectedDate.setDate(fn(selectedDate.getDate(), 7));
    const selectedDateFormatted = formatDate(selectedDate);

    onChangeSelectedPeriod(selectedDateFormatted);
  }

  function handleMoveByMonth(direction: Direction) {
    const fn = directionToFunction[direction];
    const selectedDate = new Date(selectedPeriod);
    selectedDate.setMonth(fn(selectedDate.getMonth(), 1));
    const selectedDateFormatted = formatDate(selectedDate);
    const dateSplit = selectedDateFormatted.split('-');
    const monthValue = `${dateSplit[0]}-${dateSplit[1]}`;

    onChangeSelectedPeriod(monthValue);
  }

  function handleMoveByYear(direction: Direction) {
    const fn = directionToFunction[direction];
    const selectedDate = new Date(selectedPeriod);
    const selectedYear = fn(selectedDate.getFullYear(), 1);

    onChangeSelectedPeriod(selectedYear.toString());
  }

  function handleMove(direction: Direction) {
    const rangeTypeToCaller: Record<DateRangeType, (direction: Direction) => void> = {
      [DateRangeType.Day]: handleMoveByDay,
      [DateRangeType.Week]: handleMoveByWeek,
      [DateRangeType.Month]: handleMoveByMonth,
      [DateRangeType.Year]: handleMoveByYear,
      [DateRangeType.All]: () => undefined,
    };
    const fn = rangeTypeToCaller[rangeType];
    fn(direction);
  }

  function checkIfMoveBackButtonIsDisabled() {
    if (rangeType === DateRangeType.Year && selectedPeriod === FIRST_YEAR_OF_DATA.toString()) return true;
    return rangeType === DateRangeType.All || isDisabled;
  }

  function checkIfMoveForwardButtonIsDisabled() {
    if (rangeType === DateRangeType.Week) {
      // Increment the selected period by 7 days, and check if that date is after today.
      // If so, the user can't move forward.
      const selectedDate = new Date(selectedPeriod);
      selectedDate.setDate(selectedDate.getDate() + 7);
      const today = new Date();

      if (selectedDate > today) return true;
    }

    return rangeType === DateRangeType.All || isDisabled || selectedPeriod === INITIAL_SELECTED_PERIOD_STATE[rangeType];
  }

  return (
    <Flex py={[2, 2, 0]} align={'center'} justify={['space-between', 'space-between', 'center']}>
      <IconButton
        data-testid={'fleet-dashboard-prev-date-chevron'}
        size={isMobileViewport ? 'sm' : 'md'}
        rounded={20}
        mr={2}
        variant={'ghost'}
        aria-label="Previous"
        icon={<ChevronLeftIcon h={5} w={5} />}
        isDisabled={checkIfMoveBackButtonIsDisabled()}
        onClick={() => handleMove('back')}
      />
      {selectedRangeTypeToInputComponent[rangeType]}
      <Flex>
        <IconButton
          data-testid={'fleet-dashboard-next-date-chevron'}
          size={isMobileViewport ? 'sm' : 'md'}
          rounded={20}
          ml={2}
          variant={'ghost'}
          aria-label="Next"
          icon={<ChevronRightIcon h={5} w={5} />}
          onClick={() => handleMove('forward')}
          isDisabled={checkIfMoveForwardButtonIsDisabled()}
        />
        {isMobileViewport && (
          <IconButton
            data-testid={'fleet-dashboard-current-date-mobile-arrow'}
            size={isMobileViewport ? 'sm' : 'md'}
            rounded={20}
            ml={2}
            variant={'ghost'}
            aria-label="Open date picker"
            icon={<ArrowRightIcon h={3} w={3} />}
            isDisabled={rangeType === DateRangeType.All || isDisabled}
            onClick={() => onChangeSelectedPeriod(INITIAL_SELECTED_PERIOD_STATE[rangeType])}
          />
        )}
      </Flex>
    </Flex>
  );
}
